import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Removed duplicate MyGroupSessionsPage - using GroupSessions with filterMode instead
import FacilitatorGroupSessionsPage from './pages/facilitator-group-sessions-academy';
import { useAuth } from '@/core/auth/auth-context';
import { UserRole } from './types/enums';

// Lazy-loaded page components for code splitting
const Dashboard = lazy(() => import('./pages/dashboard-simple'));
// const CourseViewer = lazy(() => import('./features/facilitator-training/pages/CourseViewer')); // Different component, not used
const TrainingCourseViewer = lazy(() => import('./pages/facilitator/training/course-viewer-academy-style'));
const Profile = lazy(() => import('./pages/profile-academy-style'));
const GroupSessions = lazy(() => import('./pages/group-sessions'));
const JoinSession = lazy(() => import('./pages/join-session'));
// Using GroupSessionDetail instead of SessionDetail for all routes
// SessionCreate removed - using GroupSessionCreateSimple instead
const GroupSessionCreateSimple = lazy(() => import('./pages/group-session-create-simple-academy'));
const GroupSessionEdit = lazy(() => import('./pages/group-session-edit-enhanced'));
const ActivityCreate = lazy(() => import('./pages/activity-create-simple'));
const ActivityEdit = lazy(() => import('./pages/activity-edit'));
const Login = lazy(() => import('./pages/auth/login'));
const ResetPassword = lazy(() => import('./pages/auth/reset-password'));
const UpdatePassword = lazy(() => import('./pages/auth/update-password'));
const SignupComplete = lazy(() => import('./pages/auth/signup-complete'));
const AcceptInvitation = lazy(() => import('./pages/auth/accept-invitation'));
const RetrieveTempPassword = lazy(() => import('./pages/auth/retrieve-temp-password'));
const NotFound = lazy(() => import('./pages/not-found'));
// New Session Explorer feature
const SessionExplorer = lazy(() => import('./pages/session-explorer/index'));
// New Group Session Detail V2 with enhanced UI
const GroupSessionDetailV2 = lazy(() => import('./pages/group-session-detail-v2-academy'));
// Invitation system
const InvitationHandler = lazy(() => import('./pages/invitation-handler-with-consent'));


// Admin pages
const UserManagement = lazy(() => import('./pages/admin/user-management'));
const UsageMetricsDashboard = lazy(() => import('@/features/admin/components/usage-metrics-dashboard'));
const AdminTemplates = lazy(() => import('./pages/admin/templates'));
const AdminActivityTemplates = lazy(() => import('./pages/admin/activity-templates'));
const TrainingReports = lazy(() => import('./pages/admin/training-reports'));
const TrainingFeedbackAnalytics = lazy(() => import('./pages/admin/training-feedback-analytics'));
const TrainingEnrollmentManagement = lazy(() => import('./pages/admin/training-enrollment-management'));
const TrainingManagement = lazy(() => import('./pages/admin/training-management'));
const AdminDashboardV2 = lazy(() => import('./pages/admin/admin-dashboard-v2'));
const PrivacyDashboard = lazy(() => import('./pages/admin/privacy-dashboard'));

// Facilitator Training module - removed old TrainingPage, using course viewer instead

// Learning Academy modules
const LearningAcademy = lazy(() => import('./features/facilitator-training/pages/LearningAcademy'));
// These components are imported inside LearningAcademy as needed
// const AcademyCourses = lazy(() => import('./features/facilitator-training/pages/AcademyCourses'));
// const AcademyChat = lazy(() => import('./features/facilitator-training/components/AcademyChat'));

// New public pages (no auth required)
const Welcome = lazy(() => import('./pages/welcome'));
const PublicSessions = lazy(() => import('./pages/public-sessions'));
// Removed duplicate GroupSessionCreate - using GroupSessionCreateSimple instead
const PublicActivityDetail = lazy(() => import('./pages/public-activity-detail'));
const PublicArtifacts = lazy(() => import('./pages/public-artifacts'));

// Report Pages
const ReportDetail = lazy(() => import('./pages/report-detail'));

// Test Pages
const TestTranscriptionStatus = lazy(() => import('./pages/test-transcription-status'));
const TestBlankPage = lazy(() => import('./pages/test-blank-page'));
const CourseViewerDebug = lazy(() => import('./pages/facilitator/training/course-viewer-debug'));
const CourseViewerSimple = lazy(() => import('./pages/facilitator/training/course-viewer-simple'));

// Training Pages
const AcceptTrainingInvitation = lazy(() => import('./pages/facilitator/training/accept-invitation'));
const MyTrainingFeedback = lazy(() => import('./pages/facilitator/training/my-feedback'));

// Participant Pages
const ParticipantReportFeedback = lazy(() => import('./pages/participant-report-feedback'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/legal/privacy-policy'));
const TermsOfService = lazy(() => import('./pages/legal/terms-of-service'));
const CookiePolicy = lazy(() => import('./pages/legal/cookie-policy'));

// Wisdom Card Pages
const WisdomCardParticipate = lazy(() => import('./pages/wisdom-card/participate'));
const WisdomCardResults = lazy(() => import('./pages/wisdom-card/results'));

// MetCard Pages
const MetCardJoinSession = lazy(() => import('./features/metcard/pages/MetCardJoinSession'));
const MetCardParticipantView = lazy(() => import('./features/metcard/pages/MetCardParticipantView'));
const MetCardSessionList = lazy(() => import('./features/metcard/pages/MetCardSessionList'));
const MetCardSessionCreate = lazy(() => import('./features/metcard/pages/MetCardSessionCreate'));
const MetCardSessionDetail = lazy(() => import('./features/metcard/pages/MetCardSessionDetail'));
const MetCardFacilitatorView = lazy(() => import('./features/metcard/pages/MetCardFacilitatorView'));
const MetCardAnalytics = lazy(() => import('./features/metcard/pages/MetCardAnalytics'));


// Loading component for suspense fallback
const Loading = () => <div className="flex items-center justify-center h-screen">Loading...</div>;

/**
 * Protected Route component that redirects to login if not authenticated
 * and enforces role-based access control
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  redirectTo?: string;
}

function ProtectedRoute({ 
  children, 
  requiredRoles = [], 
  redirectTo = '/dashboard' 
}: ProtectedRouteProps) {
  const { user, userRole, loading } = useAuth();
  
  

  if (loading) {

    return <Loading />;
  }

  if (!user) {

    // Preserve the current URL as the returnTo parameter
    const currentPath = window.location.pathname + window.location.search;
    return <Navigate to={`/auth/login?returnTo=${encodeURIComponent(currentPath)}`} replace />;
  }
  
  

  // Strict role-based check (no email overrides)
  // This ensures user roles from database are strictly enforced
  const isAdminByEnum = userRole === UserRole.ADMIN;
  const isAdminByString = String(userRole).toLowerCase() === String(UserRole.ADMIN).toLowerCase();
  // Removed email-based override for security reasons
  const isAdmin = isAdminByEnum || isAdminByString;

  // Always allow admin access to any protected route (using enhanced check)
  if (isAdmin) {

    return <>{children}</>;
  }
  
  // If no specific roles are required, allow access to any authenticated user
  if (requiredRoles.length === 0) {
    return <>{children}</>;
  }
  
  // Enhanced role check to handle different role value types
  const hasRequiredRole = requiredRoles.some(role => {
    const roleMatches = String(userRole).toLowerCase() === String(role).toLowerCase();

    return roleMatches;
  });
  
  // Check if user has one of the required roles
  if (!hasRequiredRole) {

    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

// Cast lazy loaded component to include filterMode prop
const GroupSessionsComponent = GroupSessions as React.FC<{ filterMode?: 'all' | 'my' | 'assigned' }>;

// Wrapper components using the correctly typed GroupSessionsComponent
const MySessionsPage = () => <GroupSessionsComponent filterMode="my" />;
const AssignedSessionsPage = () => <GroupSessionsComponent filterMode="assigned" />;

/**
 * Main application routes
 */
export function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes - no authentication required */}
        {/* PROTECTED ROUTE: Main landing page - Do not remove */}
        <Route path="/" element={<Welcome />} />
        <Route path="/join-session" element={<JoinSession />} />
        <Route path="/join-session/:sessionId" element={<JoinSession />} />
        <Route path="/public/sessions" element={<PublicSessions />} />
        <Route path="/public/sessions/create" element={<GroupSessionCreateSimple />} />
        <Route path="/public/sessions/create-simple" element={<GroupSessionCreateSimple />} />
        <Route path="/public/sessions/:sessionId/activities/:activityId" element={<PublicActivityDetail />} />
        <Route path="/public/sessions/:sessionId/activities/:activityId/artifacts" element={<PublicArtifacts />} />
        
        {/* Invitation handler route */}
        <Route path="/invitations/:invitationId" element={<InvitationHandler />} />
        
        {/* Participant report feedback route - no auth required, uses token */}
        <Route path="/reports/:reportId/feedback" element={<ParticipantReportFeedback />} />
        
        {/* Legal pages - publicly accessible */}
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms-of-service" element={<TermsOfService />} />
        <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
        
        {/* Wisdom Card participation - publicly accessible */}
        <Route path="/wisdom-card/participate/:accessCode" element={<WisdomCardParticipate />} />

        {/* Wisdom Card results - publicly accessible with session ID */}
        <Route path="/wisdom-card/results/:sessionId" element={<WisdomCardResults />} />

        {/* MetCard routes - publicly accessible for participants */}
        <Route path="/metcard/join/:accessCode" element={<MetCardJoinSession />} />
        <Route path="/metcard/participate/:sessionId" element={<MetCardParticipantView />} />
        
        {/* PROTECTED: Auth routes - critical authentication paths - modify with extreme caution */}
        {/* Login page - main entry point for all user authentication */}
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/update-password" element={<UpdatePassword />} />
        <Route path="/auth/signup-complete" element={<SignupComplete />} />
        <Route path="/auth/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/invitations/accept" element={<AcceptInvitation />} />
        <Route path="/auth/retrieve-temp-password" element={<RetrieveTempPassword />} />

        
        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <AdminDashboardV2 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard-v2"
          element={<Navigate to="/admin" replace />}
        />
        <Route
          path="/admin/users" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/usage-metrics" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <UsageMetricsDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/templates" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <AdminTemplates />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/activity-templates" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <AdminActivityTemplates />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/training-reports" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <TrainingReports />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/training" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <TrainingManagement />
            </ProtectedRoute>
          } 
        />
        {/* Legacy routes - redirect to unified training management */}
        <Route 
          path="/admin/training-feedback-analytics" 
          element={<Navigate to="/admin/training" replace />}
        />
        <Route 
          path="/admin/training-enrollment" 
          element={<Navigate to="/admin/training" replace />}
        />
        <Route 
          path="/admin/privacy" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <PrivacyDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-group-sessions" 
          element={
            <ProtectedRoute>
              {/* Dedicated page showing only sessions where the user is a member */}
              <AssignedSessionsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/facilitator/group-sessions" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              {/* Facilitator-specific view with create session functionality */}
              <FacilitatorGroupSessionsPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Route for admins to view all sessions without filtering */}
        <Route 
          path="/group-sessions" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <GroupSessionsComponent filterMode="all" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/group-sessions/:sessionId" 
          element={
            <ProtectedRoute>
              {/* New enhanced Group Session Detail UI with tree navigation */}
              <GroupSessionDetailV2 />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/session-explorer/:sessionId" 
          element={
            <ProtectedRoute>
              {/* Legacy session explorer view */}
              <SessionExplorer />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/group-sessions/:id/edit" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FACILITATOR]}>
              <GroupSessionEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/group-sessions/create" 
          element={
            <ProtectedRoute>
              <GroupSessionCreateSimple />
            </ProtectedRoute>
          } 
        />
        {/* PROTECTED: Group Session Creation - critical application flow */}
        <Route 
          path="/group-sessions/create-simple" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FACILITATOR]} redirectTo="/group-sessions">
              <GroupSessionCreateSimple />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/group-sessions/:sessionId/activities/create" 
          element={
            <ProtectedRoute>
              <ActivityCreate />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/activities/:activityId/edit" 
          element={
            <ProtectedRoute>
              <ActivityEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-sessions" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]} redirectTo="/dashboard">
              <MySessionsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/assigned-sessions" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.USER, UserRole.ADMIN]} redirectTo="/dashboard">
              <AssignedSessionsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Report routes */}
        <Route 
          path="/reports/:reportId" 
          element={
            <ProtectedRoute>
              <ReportDetail />
            </ProtectedRoute>
          }
        />

        {/* Test routes */}
        <Route 
          path="/test/transcription-status" 
          element={
            <ProtectedRoute>
              <TestTranscriptionStatus />
            </ProtectedRoute>
          }
        />
        <Route path="/test/blank" element={<TestBlankPage />} />
        
        {/* Training Invitation route (public) */}
        <Route 
          path="/facilitator/training/invite/:token" 
          element={<AcceptTrainingInvitation />}
        />
        
        {/* Facilitator Welcome/Onboarding */}
        <Route 
          path="/facilitator/welcome" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              {React.createElement(lazy(() => import('./pages/facilitator/welcome')))}
            </ProtectedRoute>
          }
        />
        
        {/* Facilitator Training routes */}
        {/* Redirect old training route to new course viewer */}
        <Route 
          path="/facilitator/training" 
          element={<Navigate to="/facilitator/training/course/1" replace />}
        />
        
        {/* Main training course viewer */}
        <Route 
          path="/facilitator/training/course/:chapterId?" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <TrainingCourseViewer />
            </ProtectedRoute>
          }
        />
        
        {/* Keep debug routes for development only */}
        {process.env.NODE_ENV === 'development' && (
          <>
            <Route 
              path="/facilitator/training/course-debug/:chapterId?" 
              element={<CourseViewerDebug />}
            />
            <Route 
              path="/facilitator/training/course-simple/:chapterId?" 
              element={
                <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
                  <CourseViewerSimple />
                </ProtectedRoute>
              }
            />
          </>
        )}
        
        {/* Redirect old module-based routes */}
        <Route 
          path="/facilitator/training/:moduleId" 
          element={<Navigate to="/facilitator/training/course/1" replace />}
        />
        <Route 
          path="/facilitator/training/:moduleId/:submoduleId" 
          element={<Navigate to="/facilitator/training/course/1" replace />}
        />
        <Route 
          path="/facilitator/training/my-feedback" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <MyTrainingFeedback />
            </ProtectedRoute>
          }
        />
        
        {/* Wisdom Weaver Learning Academy */}
        <Route 
          path="/facilitator/academy" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/facilitator/academy/courses" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/facilitator/academy/courses/:courseId" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/facilitator/academy/courses/:courseId/:moduleId" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/facilitator/academy/chat" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/facilitator/academy/community" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/facilitator/academy/progress" 
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facilitator/academy/settings"
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <LearningAcademy />
            </ProtectedRoute>
          }
        />

        {/* MetCard facilitator and admin routes */}
        <Route
          path="/metcard/sessions"
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <MetCardSessionList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metcard/sessions/create"
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <MetCardSessionCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metcard/sessions/:sessionId"
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <MetCardSessionDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metcard/sessions/:sessionId/facilitate"
          element={
            <ProtectedRoute requiredRoles={[UserRole.FACILITATOR, UserRole.ADMIN]}>
              <MetCardFacilitatorView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metcard/analytics"
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
              <MetCardAnalytics />
            </ProtectedRoute>
          }
        />

        {/* Fallback route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
